import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';

import {Observable} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';
import {AuthService} from "./auth.service";

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    public authService: AuthService,
    public router: Router
  ) {
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // `authState` emits only once Firebase has restored (or failed to restore) the
    // persisted session, so its first value is authoritative. The previous check read
    // localStorage, which stayed truthy for sessions Firebase had already dropped.
    return this.authService.user$.pipe(
      take(1),
      switchMap(async user => {
        if (!user) {
          // Drop the role/permission cache left over from the dead session, otherwise
          // the sidebar keeps rendering menus the user can no longer read.
          localStorage.clear();
          return this.router.createUrlTree(['login']);
        }

        // Firebase restores the session on reload, but the identity keys are written
        // during SignIn only. Components read `userData` and `permissions` straight
        // out of localStorage as they initialise, so the route must not render until
        // the cache has been refilled for this session.
        if (!localStorage.getItem('userData') || !localStorage.getItem('permissions')) {
          const userData = await this.authService.hydrateUserData(user);
          if (!userData) {
            // Signed in against a user record we cannot read — nothing in the app
            // will work without it, so send them back to login rather than render
            // a shell with no role or permissions.
            localStorage.clear();
            return this.router.createUrlTree(['login']);
          }
        }

        return true;
      })
    );
  }
}
